import {
  AllCommunityModule,
  ColDef,
  ICellRendererParams,
  ModuleRegistry,
  GridApi,
  GridReadyEvent,
  ColDefField,
  IFilterParams,
  IDoesFilterPassParams,
  FilterOpenedEvent,
  FilterModifiedEvent,
  IFilterComp,
  FilterModel,
  Column,
  RowClickedEvent,
  FilterChangedEvent,
} from 'ag-grid-community'
import { AgGridReact, useGridFilter } from 'ag-grid-react'
import {
  addOneMinute,
  BOOLEAN_FILTER_OPTIONS,
  columnTypesConst,
  DATE_FILTER_OPERATORS,
  // DropdownType,
  FIELD_VALUE_CONSTANTS,
  filterOperatorValues,
  formatValueWithPattern,
  getMinutesFromTime,
  getOperatorFromKey,
  getOperatorKey,
  NAMESPACE,
  NUMERIC_FILTER_OPERATORS_AG_GRID,
  OnActionClickCallbackType,
  OnColumnClickCallbackType,
  OnFilterChangeCallbackType,
  OnRowClickCallbackType,
  SORT_KEYS,
  SortObjectType,
  subtractOneMinute,
  TableActionsPropsTypes,
  TableActionType,
  TableColumnType,
  TableFilterObjectBody,
  tableFilterType,
} from '../utils'
import { isEmpty, noop, debounce, cloneDeep, isEqual } from 'lodash'
import { TableActions } from './ui-table/TableActions'
import {
  createContext,
  forwardRef,
  LegacyRef,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  SvgDisableSortArrow,
  SvgFilter,
  SvgSearchIcon,
  SvgSortArrowUser,
} from '../assets/images'
import {
  getColumnStateFromStorage,
  removeSavedColumnStateFromStorage,
  saveColumnStateToStorage,
} from '../utils/functions'
import { TableDataColumn, TableHeaderColumns } from './ui-table/TableColumn'
import Pagination from './Pagination'
import moment from 'moment'
import CustomRadioGroup from './CustomRadioGroup'
import Tooltip from './Tooltip'
import TableColumnChooser from './ui-table/TableColumnChooser'
import { FieldValues } from 'react-hook-form'
import CustomButton from './CustomButton'

export interface CustomColDef<T> extends ColDef<T> {
  columnData?: TableColumnType<T>
  onColumnClick?: OnColumnClickCallbackType<T>
  onSortClick: (key: string, isAsc: SORT_KEYS) => void
  onFilterChange?: OnFilterChangeCallbackType
  operatorRef?: React.MutableRefObject<
    | {
        [key: string]: {
          label: string
          value: string
        }
      }
    | undefined
  >
  headerValues?: T
}

interface CustomFilterParams<T> extends Omit<IFilterParams<T>, 'column'> {
  column: CustomColDef<T>
}

ModuleRegistry.registerModules([AllCommunityModule])

interface AgGridWrapperProps<T> {
  data: T[]
  columns: TableColumnType<T>[]
  actions?: TableActionType<T>[]
  onActionClick?: OnActionClickCallbackType<T>
  disabledKey?: keyof T
  tableName: string
  pageName: string
  headerValues?: T
  onSortClick?: (key: string, isAsc: SORT_KEYS) => void
  sort?: SortObjectType | null
  loading?: boolean
  onFilterChange?: OnFilterChangeCallbackType
  onColumnClick?: OnColumnClickCallbackType<T>
  emptyDataMessage?: string
  children?: ReactNode
  stickyActions?: boolean
  showTableColumnOnEmpty?: boolean
  minHeight?: string | number
  maxHeight?: string | number
  noBackgroundHover?: boolean
  onRowClick?: OnRowClickCallbackType<T>
  totalPages?: number
  rowsPerPage?: number
  onPageChange?: (page: number, rowsPerPage: number) => void
  onRowsChange?: (rowsPerPage: number) => void
  dataQaId?: string
  filterObject?: TableFilterObjectBody
  isColumnChooser?: boolean
  onColumnListUpdate?: (cols: TableColumnType<T>[]) => void
  // restoreDefault?: () => void
  showRestoreButton?: boolean
  defaultColumns?: TableColumnType<T>[]
  paginationCurrentPage?: number
  showActionHeading?: boolean
  actionHeading?: string
  showSubTotal?: boolean
  subTotalCols?: string[]
  emptyDataMessageHeight?: string
  subtotalObject?: FieldValues
  totalElements?: number
  numberOfElements?: number
}

const CustomAgTable = <T,>({
  columns,
  tableName,
  pageName,
  ...props
}: AgGridWrapperProps<T>) => {
  const [columnsState, setColumnsState] = useState<TableColumnType<T>[]>([])

  const getColId = (col: TableColumnType<T>) =>
    (col as unknown as { colId: string })?.colId ??
    (col?.accessor as unknown as string) ??
    (col?.header as string) ??
    ''

  const createColumnHash = (
    cols: TableColumnType<T>[]
  ): Map<string, TableColumnType<T>> => {
    const hash = new Map<string, TableColumnType<T>>()

    cols.forEach((col) => {
      const key = getColId(col)
      if (key) {
        hash.set(key, col)
      }
    })

    return hash
  }

  useEffect(() => {
    if (!columns.length) return
    const savedColumnState = getColumnStateFromStorage(pageName, tableName)

    if (!savedColumnState) {
      setColumnsState(columns)
      return
    }

    const columnHash = createColumnHash(columns)
    const orderedCols: TableColumnType<T>[] = []

    savedColumnState.forEach((savedCol) => {
      const originalCol = columnHash.get(savedCol.colId)

      if (originalCol) {
        orderedCols.push({
          ...originalCol,
          isHidden: !!savedCol.hide,
          width: savedCol.width,
          flex: savedCol.flex ?? null,
        })

        columnHash.delete(savedCol.colId)
      }
    })

    const remainingCols = Array.from(columnHash.values())

    const finalColumns = [...orderedCols, ...remainingCols]

    setColumnsState(finalColumns)
  }, [columns, pageName, tableName])

  return columnsState.length ? (
    <CustomAgTableInner
      columns={columnsState}
      tableName={tableName}
      pageName={pageName}
      {...props}
    />
  ) : null
}

const CustomAgTableInner = <T,>({
  data,
  columns,
  actions,
  onActionClick,
  disabledKey,
  onSortClick = noop,
  tableName,
  pageName,
  headerValues,
  loading,
  sort = null,
  onFilterChange = noop,
  onColumnClick,
  emptyDataMessage,
  children,
  stickyActions = true,
  showTableColumnOnEmpty = true,
  minHeight = '',
  maxHeight,
  noBackgroundHover = false,
  onRowClick = noop,
  totalPages,
  rowsPerPage = FIELD_VALUE_CONSTANTS.TWENTY_FIVE,
  onPageChange,
  onRowsChange,
  dataQaId,
  filterObject,
  isColumnChooser = false,
  onColumnListUpdate = noop,
  // restoreDefault,
  showRestoreButton = false,
  defaultColumns,
  paginationCurrentPage,
  showActionHeading = true,
  actionHeading = NAMESPACE?.ACTIONS,
  showSubTotal = false,
  subtotalObject = {},
  totalElements,
  numberOfElements,
}: AgGridWrapperProps<T>) => {
  const topGrid = useRef<AgGridReact>(null)
  const bottomGrid = useRef<AgGridReact>(null)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [rowPerPage, setRowPerPage] = useState<number>(
    rowsPerPage || FIELD_VALUE_CONSTANTS.TWENTY_FIVE
  )
  const gridApiRef = useRef<GridApi<T> | null>(null)
  const afterApiCall = useRef<boolean>(false)
  const columnHeader = useRef<string>()
  const isRestoring = useRef<boolean>(false)
  const [isGridReadyToRender, setIsGridReadyToRender] = useState(false)
  const [isAutoHeightDetected, setIsAutoHeightDetected] = useState<
    null | boolean
  >(null)
  const isGridReadyToRenderRef = useRef<boolean>(false)
  const operatorRef = useRef<
    | {
        [key: string]: {
          label: string
          value: string
        }
      }
    | undefined
  >({})
  const appliedTimeFilterRef = useRef<{
    [key: string]: {
      dateFrom: string
      dateTo: string
      filterType: string
      type: string
    } | null
  }>({})
  const tableRef = useRef<HTMLDivElement>(null)
  const gridWrapperRef = useRef<HTMLDivElement>(null)
  const gridWrapperRefDetected = useRef<boolean>(false)

  const isFirstRender = useRef<boolean>(true)

  const [columnsState, setColumnsState] =
    useState<TableColumnType<T>[]>(columns)
  const handleOnColUpdate = (colList: TableColumnType<T>[]) => {
    if (isEqual(colList, columnsState)) return
    setColumnsState([...colList])
    onColumnListUpdate([...colList])
    if (!gridApiRef.current) return

    const currentState = gridApiRef.current.getColumnState()
    const componentColWidths = new Map<string, number>()
    colList.forEach((col) => {
      const colId = getColId(col)
      const currentCol = currentState.find((s) => s.colId === colId)
      if (currentCol && currentCol.width) {
        componentColWidths.set(colId, currentCol.width)
      }
    })

    const desiredState = colList.map((c) => {
      const colId = getColId(c)
      return {
        colId: colId,
        hide: !!c.isHidden,
        width: componentColWidths.has(colId)
          ? componentColWidths.get(colId)!
          : c.width ?? 200,
        pinned: c.pinned ?? null,
        flex: c.flex ?? null,
      }
    })

    const existing = gridApiRef.current.getColumnState() ?? []
    existing.forEach((s) => {
      if (!desiredState.some((d) => d.colId === s.colId)) {
        desiredState.push({
          colId: s.colId,
          hide: s.hide ?? false,
          width: s.width ?? 200,
          pinned: s.pinned ?? null,
          flex: s.flex ?? null,
        })
      }
    })
    gridApiRef.current.applyColumnState({
      state: desiredState,
      applyOrder: true,
    })
    saveColumnStateToStorage(
      pageName,
      tableName,
      desiredState
        .map((col) => ({ ...col, sort: undefined }))
        .filter((col) => col.colId !== 'actions')
    )
  }
  const generateUniqueKey = useMemo(() => {
    let key = `${pageName}`
    columnsState.forEach((c) => {
      key += `-${String(c.accessor)}`
    })
    return key
  }, [columnsState, pageName])

  useEffect(() => {
    const isSameColumns =
      columns.length === columnsState.length &&
      columns.every((col, i) => getColId(col) === getColId(columnsState[i]))
    if (!isSameColumns) {
      setColumnsState([...columns])
    }
  }, [columns])

  const setGridReadyToRender = () => {
    if (!gridApiRef.current) return

    const firstVisibleCol = gridApiRef.current
      .getColumns()
      ?.find((col) => col.isVisible())

    if (firstVisibleCol) {
      gridApiRef.current.ensureColumnVisible(firstVisibleCol.getColId())
    }

    isGridReadyToRenderRef.current = true
    setIsGridReadyToRender(true)
  }

  const getFilterType = (filterData: TableColumnType<T>['filterData']) => {
    if (!filterData) return false
    if (filterData.dataType === tableFilterType.DROPDOWN || filterData.onFocus)
      return CustomDropdownFilter
    else if (
      filterData?.dataType === 'string' ||
      filterData?.dataType === 'string-dynamic'
    )
      return true
    else if (isDateTypeOperator(filterData.dataType))
      return 'agDateColumnFilter'
    else if (filterData.dataType === tableFilterType.NUMBER)
      return 'agNumberColumnFilter'
    else if (filterData.dataType === tableFilterType.BOOLEAN)
      return CustomBooleanFilter
    return false
  }

  const getFilterTypeReverse = (
    filterData: TableColumnType<T>['filterData']
  ) => {
    if (!filterData) return false
    if (filterData.dataType === tableFilterType.DROPDOWN || filterData.onFocus)
      return 'dropdown'
    else if (
      filterData?.dataType === 'string' ||
      filterData?.dataType === 'string-dynamic'
    )
      return 'text'
    else if (isDateTypeOperator(filterData.dataType)) return 'date'
    else if (filterData.dataType === tableFilterType.NUMBER) return 'number'
    else if (filterData.dataType === tableFilterType.BOOLEAN) return 'boolean'
    return ''
  }

  const getColId = (col: TableColumnType<T>) =>
    (col as unknown as { colId: string })?.colId ??
    (col?.accessor as unknown as string) ??
    (col?.header as string) ??
    ''

  const TableCellRender = ({
    params,
    accessor,
  }: {
    params: ICellRendererParams
    accessor?: string | number | symbol
  }) => {
    const { columns, onColumnClick } = useContext(CustomAgTableContext)
    const col = columns?.find((c) => c.accessor === accessor)
    return (
      col && (
        // <TableActions
        //   row={params?.data}
        //   rowIndex={rowIndex}
        //   actions={actions || []}
        //   onActionClick={onActionClick || noop}
        //   setRowPosition={noop}
        //   isDisabled={params?.data?.[disabledKey || '']}
        // />
        <TableDataColumn
          column={col}
          row={params?.data}
          rowIndex={params.node.rowIndex ?? -1}
          onColumnClick={onColumnClick}
          isDisabled={
            disabledKey ? Boolean(params?.data?.[disabledKey]) : false
          }
          tabIndex={0}
        />
      )
    )
  }

  const columnDefs: CustomColDef<T>[] = useMemo(() => {
    return columnsState?.map((col) => {
      const colDef: CustomColDef<T> = {
        headerName: col.header,
        field: col.accessor as unknown as ColDefField<T>,
        filter: getFilterType(col.filterData),
        sortable: col.isSortable,
        lockVisible: true,
        columnData: col,
        hide: !!col.isHidden,
        colId: getColId(col),
        onSortClick: onSortClick,
        onColumnClick: onColumnClick,
        onFilterChange: onFilterChange,
        operatorRef: operatorRef,
        headerValues: headerValues,
        flex: col.flex,
        cellRenderer: (params: ICellRendererParams) => {
          return (
            <TableCellRender
              params={params}
              accessor={col.accessor as string}
            />
          )
        },
      }
      if (col.type === 'component') {
        colDef.minWidth = 0
        colDef.flex = null
      }
      if (col.filterData?.dataType) {
        colDef.filterParams = {
          maxNumConditions: 10,
          suppressAndOrCondition: false,
          buttons: ['reset'],
          debounceMs: 1200,
        }
        if (isDateTypeOperator(col.filterData.dataType)) {
          colDef.filterParams = {
            maxNumConditions: 1,
            suppressAndOrCondition: false,
            buttons: ['apply', 'reset'],
            browserDatePicker: true,
            maxValidYear: 9999,
            includeTime: col.filterData.dataType === tableFilterType.DATE_TIME,
            filterOptions: DATE_FILTER_OPERATORS.map((op) => {
              let numInputs = 1
              if (isRangeOperator(op.value)) {
                numInputs = 2
              }
              if (
                op.value === filterOperatorValues.IS_NULL ||
                op.value === filterOperatorValues.IS_NOT_NULL
              ) {
                numInputs = 0
              }
              return {
                displayKey: op.value,
                displayName: op.label,
                predicate: () => true,
                numberOfInputs: numInputs,
              }
            }),
          }
        } else if (col.filterData.dataType === tableFilterType.NUMBER) {
          colDef.filterParams = {
            ...colDef.filterParams,
            filterOptions: NUMERIC_FILTER_OPERATORS_AG_GRID,
            maxNumConditions: 1,
          }
        }
      }
      return colDef
    })
  }, [columnsState, headerValues])

  const CustomActionHeader = (props: { showActionHeading?: boolean }) => {
    const { showActionHeading = true } = props
    const { columnsState } = useContext(
      CustomAgTableContext as React.Context<CustomAgTableContextType<T>>
    )
    return (
      <div className="relative flex items-center justify-center w-full gap-2">
        {showActionHeading && (
          <span className="text-xs font-normal truncate">{actionHeading}</span>
        )}
        {isColumnChooser && (
          <div className="flex justify-end">
            <Tooltip content="Add column" position="left" className="max-w-28">
              <TableColumnChooser
                currentCol={columnsState ?? []}
                onColumnListUpdate={handleOnColUpdate}
                restoreDefault={restoreDefault}
                showRestoreButton={showRestoreButton}
              />
            </Tooltip>
          </div>
        )}
      </div>
    )
  }

  const modifiedColumnDefs = useMemo(() => {
    const baseDefs = columnDefs?.map((col, index) => ({
      ...col,
      colId: col.colId ?? col.field ?? col.headerName,
      sortable: false,
      headerComponent: CustomHeader,
      cellDataType: false,
      headerComponentParams: {
        displayName: `${tableName}_header_${index}`,
        column:
          col.columnData?.type === 'component' &&
          col.columnData?.header !== 'checkbox'
            ? { ...col, minWidth: 100 }
            : col,
        setSort: (sort: SORT_KEYS) => {
          onSortClick(col.colId ?? col.field ?? col.headerName ?? '', sort)
        },
        enableFilterButton: col.sortable,
      },
      resizable:
        col.columnData?.type !== 'component' ||
        col.columnData?.header !== 'checkbox',
    }))

    if (isEmpty(actions) && !isColumnChooser) return baseDefs

    return [
      ...baseDefs,
      {
        headerName: NAMESPACE?.ACTIONS,
        headerComponent: CustomActionHeader,
        headerComponentParams: {
          showActionHeading: showActionHeading && !isEmpty(actions),
        },
        colId: 'actions',
        cellRenderer: (params: ICellRendererParams) => {
          const isSensitiveField = (row: {
            is_client_controlled: boolean | string
          }): boolean => {
            return (
              row?.is_client_controlled === true ||
              row?.is_client_controlled === 'true'
            )
          }
          const isSensitive =
            isSensitiveField(
              params?.data as { is_client_controlled: boolean | string }
            ) && localStorage.getItem('has_sensitive_permission') !== 'true'
          return (
            <TableActionsWrapper
              row={params?.data}
              rowIndex={params.node.rowIndex ?? -1}
              setRowPosition={noop}
              isDisabled={params?.data?.[disabledKey || ''] || isSensitive}
            />
          )
        },
        flex: 1,
        maxWidth: isEmpty(actions) ? 50 : undefined,
        minWidth: 50,
        resizable: !isEmpty(actions),
        pinned: stickyActions ? 'right' : undefined,
        suppressSizeToFit: true,
        suppressMovable: true,
      } as CustomColDef<T>,
    ]
  }, [actions?.length, columnDefs?.length, disabledKey, generateUniqueKey])

  const onGridReady = useCallback((params: GridReadyEvent<T>) => {
    gridApiRef.current = params.api
    handleFirstDataRendered()
  }, [])

  const convertUtcToLocalString = (value: string): string => {
    const date = new Date(value)

    const localDate = [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0'),
    ].join('-')

    const localTime = date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })

    return `${localDate} ${localTime}`
  }

  const getGridFilter = (filterObject: TableFilterObjectBody): FilterModel => {
    const filterModel: FilterModel = {}
    if (filterObject?.search_criteria) {
      const mapper: Record<string, TableColumnType<T> | undefined> = {}
      columns.forEach((col) => {
        mapper[(col.accessor as string).toLocaleLowerCase()] = col
      })
      Object.values(filterObject.search_criteria).forEach((criteria) => {
        const column = mapper[criteria.search_key.toLocaleLowerCase()]
        if (!column) return
        if (!filterModel[column.accessor as string]) {
          filterModel[column.accessor as string] = {
            filterType: getFilterTypeReverse(column.filterData),
          }
          if (isDateTypeOperator(column.filterData?.dataType ?? '')) {
            if (isDateOperator(column.filterData?.dataType ?? '')) {
              filterModel[column.accessor as string] = {
                filterType: 'date',
                type: criteria.operator,
                dateFrom: `${criteria.search_value[0]} 00:00:00`,
              }
              if (criteria.search_value.length > 1) {
                filterModel[
                  column.accessor as string
                ].dateTo = `${criteria.search_value[1]} 00:00:00`
              }
            } else {
              // datetime-local and time
              const value = criteria.search_value?.[0]
              if (value) {
                const formatted = convertUtcToLocalString(value)

                filterModel[column.accessor as string] = {
                  filterType: 'date',
                  type: criteria.operator,
                  dateFrom: formatted,
                }

                if (criteria.search_value.length > 1) {
                  const toFormatted = convertUtcToLocalString(
                    criteria.search_value[1]
                  )

                  filterModel[column.accessor as string].dateTo = toFormatted
                }
              }
            }
          } else {
            const operator = isStringTypeOperator(
              column.filterData?.dataType ?? ''
            )
              ? getOperatorFromKey(criteria.operator)
              : criteria.operator
            if (
              [
                'IS_NULL',
                'IS_NOT_NULL',
                'IS_EMPTY_NULL',
                'IS_NOT_EMPTY_NOT_NULL',
              ].includes(criteria.operator)
            ) {
              filterModel[column.accessor as string].type = operator
              filterModel[column.accessor as string].filter = null
              filterModel[column.accessor as string].conditions = undefined
            } else if (
              criteria.search_value.length > 1 &&
              isStringTypeOperator(column.filterData?.dataType ?? '')
            ) {
              filterModel[column.accessor as string].operator = 'OR'
              filterModel[column.accessor as string].conditions = []
              criteria.search_value.forEach((value) => {
                filterModel[column.accessor as string].conditions.push({
                  filterType: getFilterTypeReverse(column.filterData),
                  type: getOperatorFromKey(criteria.operator),
                  filter: value,
                })
              })
            } else {
              filterModel[column.accessor as string].type = operator
              filterModel[column.accessor as string].filter =
                isStringTypeOperator(column.filterData?.dataType ?? '')
                  ? criteria.search_value[0]
                  : criteria.search_value
              filterModel[column.accessor as string].conditions = undefined
            }
          }
        } else {
          if (!filterModel[column.accessor as string].conditions) {
            filterModel[column.accessor as string].conditions = []
            filterModel[column.accessor as string].operator = 'AND'
            filterModel[column.accessor as string].conditions?.push({
              filterType: getFilterTypeReverse(column.filterData),
              type: isStringTypeOperator(column.filterData?.dataType ?? '')
                ? getOperatorFromKey(criteria.operator)
                : criteria.operator,
              filter: filterModel[column.accessor as string].filter,
            })
          }
          filterModel[column.accessor as string].conditions?.push({
            filterType: getFilterTypeReverse(column.filterData),
            type: isStringTypeOperator(column.filterData?.dataType ?? '')
              ? getOperatorFromKey(criteria.operator)
              : criteria.operator,
            filter: criteria.search_value[0],
          })
        }
      })
    }
    return filterModel
  }

  const autoSize = () => {
    setTimeout(() => {
      if (
        tableRef.current?.querySelector(
          '.ag-body-horizontal-scroll.ag-invisible'
        ) &&
        !getColumnStateFromStorage(pageName, tableName)
      ) {
        const columnsToResize: string[] = []
        let gridWidth =
          (
            tableRef.current?.querySelector(
              '.ag-body-viewport.ag-layout-normal'
            ) as HTMLElement
          )?.offsetWidth ?? 0
        gridApiRef.current?.getColumnState().forEach((col) => {
          if (
            columns.find((c) => getColId(c) === col.colId)?.type ===
              'component' ||
            col.colId === 'actions'
          ) {
            gridWidth = gridWidth - (col.width ?? 200)
          } else {
            columnsToResize.push(col.colId)
          }
        })
        const colWidth = Math.floor(gridWidth / columnsToResize.length)
        gridApiRef.current?.applyColumnState({
          state: columnsToResize.map((colId) => ({
            colId,
            width: colWidth,
          })),
        })
      }
    }, 200)
  }

  const waitForActionsCellInner = (
    observer: MutationObserver | undefined = undefined
  ) => {
    const allRows =
      tableRef.current?.querySelectorAll(
        '.ag-pinned-right-cols-container .ag-row'
      ) ?? []
    if (allRows.length === 0) {
      autoSize()
      return
    }

    let maxCellWidth = 0

    allRows.forEach((row) => {
      const actionButtons = row.querySelectorAll(
        '[col-id="actions"] td > div > *'
      )
      if (actionButtons.length === 0) return

      let totalWidth = 0
      actionButtons.forEach((btn) => {
        totalWidth += (btn as HTMLElement).offsetWidth
      })
      const gap = (actionButtons.length - 1) * 8
      const padding = 32
      const cellWidth = totalWidth + gap + padding

      if (cellWidth > maxCellWidth) maxCellWidth = cellWidth
    })

    if (maxCellWidth === 0) {
      autoSize()
      return
    }

    observer?.disconnect()

    let finalWidth = maxCellWidth
    const actionHeader = tableRef.current?.querySelector(
      '.ag-pinned-right-header [col-id="actions"] .ag-header-cell-comp-wrapper'
    )?.children[0]?.children[0] as HTMLElement
    let headerWidth = actionHeader ? actionHeader.scrollWidth : 0
    headerWidth += 34 // padding
    if (isColumnChooser) {
      headerWidth += 32
    }
    finalWidth = Math.max(maxCellWidth, headerWidth)

    const actionsColumn = gridApiRef.current?.getColumn('actions')
    if (actionsColumn) {
      ;(actionsColumn as unknown as { minWidth: number }).minWidth = finalWidth
    }

    gridApiRef.current?.applyColumnState({
      state: [
        {
          colId: 'actions',
          width: finalWidth,
          flex: null,
        },
      ],
    })
    autoSize()
  }

  const waitForActionsCell = () => {
    const container = tableRef.current?.querySelector(
      '.ag-pinned-right-cols-container'
    )
    if (!container) return
    const observer = new MutationObserver(() => {
      waitForActionsCellInner(observer)
    })
    observer.observe(container, { childList: true, subtree: true })
    setTimeout(() => observer.disconnect(), 3000)
  }

  // When first data rendered — if saved column state exists, reorder/hide table columns to match, then apply column state
  const handleFirstDataRendered = useCallback(() => {
    if (!columnDefs.length) return
    ;(async () => {
      if (!gridApiRef.current) return
      if (
        filterObject &&
        Object.keys(filterObject).length &&
        isFirstRender.current
      ) {
        const filterModel = getGridFilter(filterObject)
        gridApiRef.current.setFilterModel(filterModel)
        handleFilterChanged(undefined, filterModel)
      }
      await sleep(200)
      const savedColumnState = getColumnStateFromStorage(pageName, tableName)
      // await sleep(100)

      if (savedColumnState) {
        // --- Sync our columnsState order + isHidden with saved state ---
        // const idToCol = new Map<string, TableColumnType<T>>(
        //   columns.map((c) => [getColId(c), c])
        // )
        // const orderedCols: TableColumnType<T>[] = []
        // savedColumnState.forEach((st) => {
        //   const found = idToCol.get(st.colId)
        //   if (found) {
        //     orderedCols.push({
        //       ...found,
        //       isHidden: !!st.hide,
        //       width: st.width,
        //       flex: st.flex ?? 0,
        //     })
        //     idToCol.delete(st.colId)
        //   }
        // })
        // // append remaining original columns not present in saved state (preserve original order)
        // const remaining = columns.filter(
        //   (c) => !savedColumnState.some((s) => s.colId === getColId(c))
        // )
        // const finalCols = [...orderedCols, ...remaining]
        // update local state to reflect saved order/hide
        // setColumnsState(finalCols)
        // apply saved state to grid (use the exact state from storage)
        isRestoring.current = true
        const success = gridApiRef.current.applyColumnState({
          state: savedColumnState
            .filter((col) => col.colId !== 'actions')
            .map((col) => ({
              ...col,
              flex: col.width !== 200 ? null : 1, // IMPORTANT
            })),
          applyOrder: true,
        })
        if (!success) {
          console.warn('Failed to apply saved column state.')
          removeSavedColumnStateFromStorage(pageName, tableName)
        }

        isRestoring.current = false
        setTimeout(() => {
          setGridReadyToRender()
          const comps = columnDefs
            .filter((col) => col.columnData?.type === 'component')
            ?.map((c) => c.colId ?? '')
          if (comps.length) {
            gridApiRef.current?.autoSizeColumns(comps)
          }
        }, 0)
      } else {
        setTimeout(() => {
          setGridReadyToRender()
          gridApiRef.current?.autoSizeAllColumns()
        }, 0)
      }
      waitForActionsCell()
    })()
  }, [pageName, tableName, columns])

  const onColumnMoved = (e: { source: string }) => {
    if (
      isRestoring.current ||
      !isGridReadyToRender ||
      e?.source !== 'uiColumnMoved'
    )
      return
    if (afterApiCall.current) {
      afterApiCall.current = false
      return
    }
    if (gridApiRef.current) {
      const newColumnState = gridApiRef.current.getColumnState()
      saveColumnStateToStorage(
        pageName,
        tableName,
        newColumnState
          .map((col) => ({ ...col, sort: undefined }))
          .filter((col) => col.colId !== 'actions')
      )
      setColumnsState((prev) => {
        const prevMap = new Map(prev.map((col) => [getColId(col), col]))

        const ordered = newColumnState
          .map((state) => prevMap.get(state.colId))
          .filter((col): col is TableColumnType<T> => !!col)

        const remaining = prev.filter(
          (col) => !newColumnState.some((s) => s.colId === getColId(col))
        )

        return [...ordered, ...remaining]
      })
    }
  }

  const onColumnResized = (e: { source: string }) => {
    if (
      gridApiRef.current &&
      isGridReadyToRender &&
      e?.source === 'uiColumnResized'
    ) {
      const newColumnState = gridApiRef.current.getColumnState()
      saveColumnStateToStorage(
        pageName,
        tableName,
        newColumnState
          .map((col) => ({ ...col, sort: undefined }))
          .filter((col) => col.colId !== 'actions')
      )
    }
  }

  const customOverlayLoadingTemplate = `
  <div style="display: flex; align-items: center; justify-content: center; height: 100%;">
    <div style="display: flex; flex-direction: column; align-items: center; gap: 8px;">
      <div class="ag-loader-spinner" style="
        border: 4px solid #f3f3f3;
        border-top: 4px solid #3498db;
        border-radius: 50%;
        width: 36px;
        height: 36px;
        animation: spin 1s linear infinite;
      "></div>
      <span style="color: #666; font-size: 14px;">Loading rows...</span>
    </div>
    <style>
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    </style>
  </div>
`
  const onFilterOpened = async (event: FilterOpenedEvent<T>) => {
    const ePopup = tableRef.current?.querySelector<HTMLElement>('.ag-filter')
    const filterInstance = await event.api.getColumnFilterInstance(
      event.column as Column
    )
    const dataType =
      (event.column as unknown as CustomColDef<T>).columnData?.filterData
        ?.dataType ?? ''
    if (!filterInstance) return
    if (
      isTimeOperator(dataType) &&
      appliedTimeFilterRef.current[
        ((event.column as unknown as CustomColDef<T>).columnData
          ?.accessor as string) ?? ''
      ]
    ) {
      await filterInstance.setModel(
        appliedTimeFilterRef.current[
          ((event.column as unknown as CustomColDef<T>).columnData
            ?.accessor as string) ?? ''
        ]!
      )
    }
    setTimeout(() => {
      if ('getColId' in event.column) {
        if (isDateTypeOperator(dataType)) {
          const dateInputs = ePopup?.querySelectorAll('input')
          if (dateInputs?.length) {
            dateInputs.forEach((dateInput, index) => {
              if (dateInput) {
                if (!isDateOperator(dataType)) {
                  if (index === 0) {
                    dateInput.value = filterInstance?.getModel()?.dateFrom ?? ''
                  } else if (index === 1) {
                    dateInput.value = filterInstance?.getModel()?.dateTo ?? ''
                  }
                }
                dateInput.step = ''
                dateInput.type = isDateOperator(dataType)
                  ? 'date'
                  : isTimeOperator(dataType)
                  ? 'time'
                  : 'datetime-local'
                if (
                  isDateTimeDynamicOperator(dataType) ||
                  isDateTimeOperator(dataType)
                ) {
                  dateInput.max = '9999-12-31T23:59'
                }
                if (
                  isTimeOperator(dataType) &&
                  !appliedTimeFilterRef.current[
                    ((event.column as unknown as CustomColDef<T>).columnData
                      ?.accessor as string) ?? ''
                  ]
                ) {
                  appliedTimeFilterRef.current[
                    ((event.column as unknown as CustomColDef<T>).columnData
                      ?.accessor as string) ?? ''
                  ] = filterInstance?.getModel() ?? null
                }
              }
            })
          }
          if (isTimeOperator(dataType)) {
            const displays = ePopup?.querySelectorAll(
              '.ag-picker-field-display'
            )
            if (displays?.length) {
              displays[0].innerHTML =
                (
                  filterInstance as unknown as CustomFilterComp<T>
                ).filterListOptions.find(
                  (op) => op.value === filterInstance?.getModel()?.type
                )?.text ??
                (filterInstance as unknown as CustomFilterComp<T>)
                  .filterListOptions[0].text
            }
          }
        }
      }
    })
    if (!ePopup) return
    const hideFilterPopup = () => {
      event?.api?.hidePopupMenu()
    }
    const resetButton = ePopup.querySelector(
      '.ag-filter-apply-panel-button[data-ref="resetFilterButton"]'
    )
    if (resetButton && event?.api) {
      resetButton.addEventListener('click', hideFilterPopup)
    }
    if (!ePopup.classList.contains('disable-other-child'))
      ePopup.classList.add('disable-other-child')
    const initialDivs = ePopup.querySelectorAll<HTMLDivElement>(
      '.ag-picker-field-display'
    )
    let num = initialDivs.length
    updateChildren(initialDivs)
    const observer = new MutationObserver(() => {
      const operatorDivs = ePopup.querySelectorAll<HTMLDivElement>(
        '.ag-picker-field-display'
      )
      if (operatorDivs.length !== num) {
        num = operatorDivs.length
        updateChildren(operatorDivs)
      }
    })
    observer.observe(ePopup, {
      childList: true,
      subtree: true,
    })
    // --- Another observer to detect popup close ---
    const popupParent = ePopup.parentElement?.parentElement?.parentElement
    if (!popupParent) return

    const popupRemovalObserver = new MutationObserver(async () => {
      const stillOpen = tableRef.current?.querySelector('.ag-filter')
      if (!stillOpen) {
        console.log('Popup closed, disconnecting observers')
        observer.disconnect()
        popupRemovalObserver.disconnect()
        if (resetButton) {
          resetButton.removeEventListener('click', hideFilterPopup)
        }
      }
    })
    popupRemovalObserver.observe(popupParent, {
      childList: true,
      subtree: false,
    })
  }

  const onFilterModified = (event: FilterModifiedEvent) => {
    const ePopup = tableRef.current?.querySelector<HTMLElement>('.ag-filter')
    if (!ePopup) return
    const initialDivs = ePopup.querySelectorAll<HTMLDivElement>(
      '.ag-picker-field-display'
    )
    updateChildren(initialDivs)
    if (!gridApiRef.current) return
    const currentModel = (
      event.filterInstance as unknown as CustomFilterComp<T>
    ).state.model

    const col = event.column as unknown as CustomColDef<T>
    const dataType = col.columnData?.filterData?.dataType
    if (dataType === 'time') {
      const prevModel = event.filterInstance.getModel() ?? {}
      const inputs = ePopup.querySelectorAll('input')
      prevModel.filterType = 'date'
      prevModel.type =
        (
          event.filterInstance as unknown as CustomFilterComp<T>
        ).filterListOptions.find((op) => op.text === initialDivs[0].innerHTML)
          ?.value ?? ''
      if (inputs[0]?.value) prevModel.dateFrom = `${inputs[0].value}`
      else prevModel.dateFrom = null
      if (inputs[1]?.value) prevModel.dateTo = `${inputs[1].value}`
      else prevModel.dateTo = null
      ;(event.filterInstance as unknown as CustomFilterComp<T>).state.valid =
        true
      ;(event.filterInstance as unknown as CustomFilterComp<T>).state.model =
        prevModel
    }

    const blankOperators = [
      NAMESPACE.BLANK,
      NAMESPACE.NOT_BLANK,
      filterOperatorValues.IS_NULL,
      filterOperatorValues.IS_NOT_NULL,
    ]
    // If user selects "Blank", keep ONLY that condition
    if (blankOperators.includes(currentModel.type ?? '')) {
      event.filterInstance.setModel({
        ...currentModel,
        conditions: undefined,
      })
      gridApiRef.current.onFilterChanged() // apply
    } else if (
      blankOperators.includes(currentModel.conditions?.[0]?.type ?? '')
    ) {
      event.filterInstance.setModel({
        ...currentModel,
        type: currentModel.conditions?.[0]?.type,
        conditions: [currentModel.conditions![0]],
      })
      gridApiRef.current.onFilterChanged() // apply
    }
  }

  const handleFilterChanged = useCallback(
    (event?: FilterChangedEvent, filterModelVal?: FilterModel) => {
      if (
        !gridApiRef.current ||
        (!isGridReadyToRenderRef.current && !filterModelVal)
      )
        return
      const filterModel = (event?.api ?? gridApiRef.current).getFilterModel()
      const apiFilter: TableFilterObjectBody = {
        search_criteria: {},
      }
      let num = 0
      Object.keys(filterModel).forEach((key) => {
        const value = filterModel[key]
        const columnDef = gridApiRef.current!.getColumnDef(key)
        const searchKey =
          (columnDef as CustomColDef<T>)?.columnData?.filterData
            ?.convertToUppercase === false
            ? key
            : key.toLocaleUpperCase()
        const isStringType = isStringTypeOperator(
          (columnDef as CustomColDef<T>)?.columnData?.filterData?.dataType ?? ''
        )
        if (value.conditions) {
          if (value.operator === 'AND') {
            value.conditions.forEach((condition: { filter: string }) => {
              apiFilter.search_criteria![num.toString()] = {
                operator: isStringType
                  ? getOperatorKey(
                      value.conditions[0].type,
                      isStringDynamicOperator(
                        (columnDef as CustomColDef<T>)?.columnData?.filterData
                          ?.dataType ?? ''
                      )
                    )
                  : value.conditions[0].type,
                search_key: searchKey,
                search_value: [condition.filter],
              }
              num++
            })
          } else {
            const values: string[] = []
            value.conditions.forEach((condition: { filter: string }) => {
              values.push(condition.filter)
            })
            apiFilter.search_criteria![num.toString()] = {
              operator: isStringType
                ? getOperatorKey(
                    value.conditions[0].type,
                    isStringDynamicOperator(
                      (columnDef as CustomColDef<T>)?.columnData?.filterData
                        ?.dataType ?? ''
                    )
                  )
                : value.conditions[0].type,
              search_key: searchKey,
              search_value: values,
            }
            num++
          }
        } else {
          if (
            isDateOperator(
              (columnDef as CustomColDef<T>)?.columnData?.filterData
                ?.dataType ?? ''
            )
          ) {
            const searchInput: string[] = []
            if (value?.dateFrom) {
              const date = value.dateFrom.split(' ')[0]
              searchInput.push(date)
            }
            if (value?.dateTo) {
              const date = value.dateTo.split(' ')[0]
              searchInput.push(date)
            }
            apiFilter.search_criteria![num.toString()] = {
              operator: value.type,
              search_key: searchKey,
              search_value: searchInput.length ? searchInput : [''],
            }
          } else if (
            isDateTypeOperator(
              (columnDef as CustomColDef<T>)?.columnData?.filterData
                ?.dataType ?? ''
            )
          ) {
            if (
              isTimeOperator(
                (columnDef as CustomColDef<T>)?.columnData?.filterData
                  ?.dataType ?? ''
              )
            ) {
              appliedTimeFilterRef.current[
                (columnDef as CustomColDef<T>)?.columnData?.accessor as string
              ] = cloneDeep(value)
            }
            const searchInput: string[] = []
            let changeRangeOperator = false
            if (
              isRangeOperator(value.type) &&
              value?.dateFrom &&
              value?.dateTo &&
              isTimeOperator(
                (columnDef as CustomColDef<T>)?.columnData?.filterData
                  ?.dataType ?? ''
              )
            ) {
              if (
                getMinutesFromTime(value.dateFrom) >
                getMinutesFromTime(value.dateTo)
              )
                return
              if (
                getMinutesFromTime(
                  getTimeInUTC(`${value.dateFrom}:00`).substring(0, 5)
                ) >
                getMinutesFromTime(
                  getTimeInUTC(`${value.dateTo}:00`).substring(0, 5)
                )
              ) {
                changeRangeOperator = true
              }
            }
            if (!isEmpty(value.dateFrom)) {
              const selectedDate = isDateTimeOperator(
                (columnDef as CustomColDef<T>)?.columnData?.filterData
                  ?.dataType ?? ''
              )
                ? moment(value.dateFrom).utc().format('YYYY-MM-DD HH:mm:ss')
                : isDateTimeDynamicOperator(
                    (columnDef as CustomColDef<T>)?.columnData?.filterData
                      ?.dataType ?? ''
                  )
                ? getDynamicDate(value.dateFrom, value.type)
                : getTimeInUTC(`${value.dateFrom}:00`)
              searchInput.push(selectedDate)
            }
            if (isRangeOperator(value.type) && value.dateTo) {
              const otherSelectedDate = isDateTimeOperator(
                (columnDef as CustomColDef<T>)?.columnData?.filterData
                  ?.dataType ?? ''
              )
                ? moment(value.dateTo).utc().format('YYYY-MM-DD HH:mm:ss')
                : isDateTimeDynamicOperator(
                    (columnDef as CustomColDef<T>)?.columnData?.filterData
                      ?.dataType ?? ''
                  )
                ? moment(new Date(value.dateTo).toISOString())
                    .utc()
                    .format('YYYY-MM-DDTHH:mm:ssZ')
                : getTimeInUTC(`${value.dateTo}:00`)

              searchInput.push(otherSelectedDate)
            }
            if (!isRangeOperator(value.type) || searchInput.length !== 1) {
              if (
                isRangeOperator(value.type) &&
                searchInput.length &&
                moment(searchInput[1]).isBefore(moment(searchInput[0]))
              ) {
                ;[searchInput[0], searchInput[1]] = [
                  searchInput[1],
                  searchInput[0],
                ]
              }
              if (changeRangeOperator) {
                apiFilter.search_criteria![num.toString()] = {
                  operator:
                    value.type === filterOperatorValues.BETWEEN
                      ? filterOperatorValues.NOT_BETWEEN
                      : filterOperatorValues.BETWEEN,
                  search_key: searchKey,
                  search_value: [
                    addOneMinute(searchInput[1]),
                    subtractOneMinute(searchInput[0]),
                  ],
                }
              } else {
                apiFilter.search_criteria![num.toString()] = {
                  operator: value.type,
                  search_key: searchKey,
                  search_value: searchInput.length ? searchInput : [''],
                }
              }
            }
          } else {
            let searchVal = []
            if (
              value.filterType === 'dropdown' ||
              value.filterType === 'boolean'
            )
              searchVal = value.filter
            else if (value.filterTo) searchVal = [value.filter, value.filterTo]
            else if (
              value.type === NAMESPACE.BLANK ||
              value.type === NAMESPACE.NOT_BLANK
            )
              searchVal = ['']
            else searchVal = [value.filter]
            apiFilter.search_criteria![num.toString()] = {
              operator: isStringType
                ? getOperatorKey(
                    value.type,
                    isStringDynamicOperator(
                      (columnDef as CustomColDef<T>)?.columnData?.filterData
                        ?.dataType ?? ''
                    )
                  )
                : value.type,
              search_key: searchKey,
              search_value: searchVal,
            }
          }
          num++
        }
      })
      onFilterChange?.(apiFilter)
      isFirstRender.current = false
    },
    []
  )

  const customOverlayNoRowsTemplate = `
    <div style="display: flex; justify-content: center; align-items: center; height: 100%;">
      <span style="color: #666; font-size: 14px;">
        ${emptyDataMessage || 'No records found'}
      </span>
    </div>
  `
  const trySizeToFit = (retry = 0) => {
    if (!gridApiRef.current || !tableRef.current) return

    const requireColumnsToFit = tableRef.current.querySelector(
      '.ag-body-horizontal-scroll.ag-invisible'
    )

    if (
      requireColumnsToFit ||
      (
        tableRef.current.querySelector(
          '.ag-body-horizontal-scroll-container'
        ) as HTMLElement
      )?.offsetWidth <
        (tableRef.current.querySelector('.ag-floating-bottom') as HTMLElement)
          ?.offsetWidth -
          (
            tableRef.current.querySelector(
              '.ag-horizontal-right-spacer'
            ) as HTMLElement
          )?.offsetWidth
    ) {
      const columnsToResize: string[] = []
      let gridWidth =
        (
          tableRef.current?.querySelector(
            '.ag-body-viewport.ag-layout-normal'
          ) as HTMLElement
        )?.offsetWidth ?? 0
      gridApiRef.current?.getColumnState().forEach((col) => {
        if (
          columns.find((c) => getColId(c) === col.colId)?.type ===
            'component' ||
          col.colId === 'actions'
        ) {
          gridWidth = gridWidth - (col.width ?? 200)
        } else {
          columnsToResize.push(col.colId)
        }
      })
      const colWidth = Math.floor(gridWidth / columnsToResize.length)
      gridApiRef.current?.applyColumnState({
        state: columnsToResize.map((colId) => ({
          colId,
          width: colWidth,
        })),
      })
    }

    if (retry < 5) {
      setTimeout(() => trySizeToFit(retry + 1), 300)
    }
  }
  const restoreDefault = useCallback(() => {
    if (!gridApiRef.current) return
    // 1. Clear ag-Grid's column state from browser storage
    removeSavedColumnStateFromStorage(pageName, tableName)
    // 2. Reset the React column state to the original defaults
    setColumnsState([...(defaultColumns ?? [])])
    // 3. Force ag-Grid to reset its internal state
    gridApiRef.current.resetColumnState()
    // 4. Apply the default state (visibility)
    const initialState = defaultColumns?.map((c) => ({
      colId: getColId(c),
      hide: !!c.isHidden,
    }))
    // Use a timeout to ensure React has rerendered before applying API calls
    setTimeout(() => {
      gridApiRef.current?.applyColumnState({
        state: initialState,
        applyOrder: true,
      })
      const comps = columnDefs
        .filter((col) => col.columnData?.type !== 'actions')
        ?.map((c) => c.colId ?? '')
      if (comps.length) {
        gridApiRef.current?.autoSizeColumns(comps)
      }
      trySizeToFit()
    }, 50)
  }, [defaultColumns, pageName, tableName]) // Add dependencies

  useDidMountEffect(() => {
    if (isGridReadyToRenderRef.current) return
    handleFirstDataRendered()
  }, [columns])

  const subTotalRowData = useMemo(
    () => [
      Object.fromEntries(
        Object.entries(subtotalObject).map(([key, value]) => [
          key,
          `SubTotal: $ ${value ?? '-'}`,
        ])
      ) as T,
    ],
    [subtotalObject]
  )

  const subTotalColumnDefs = useMemo(
    () =>
      modifiedColumnDefs.map((col) => ({
        ...col,
        cellRenderer: !subtotalObject[col.colId as string]
          ? () => <div />
          : col.cellRenderer,
      })),
    [modifiedColumnDefs, subtotalObject]
  )

  const defaultColDef = useMemo(
    () => ({
      minWidth: 100,
      flex: 0,
    }),
    []
  )
  const gridOptions = useMemo(
    () => ({
      alwaysPassFilter: () => true,
    }),
    []
  )
  const onColumnHeaderClicked = useCallback(
    (event: { column: { getId: () => string | undefined } }) => {
      columnHeader.current = event?.column?.getId()
    },
    []
  )
  const handleRowClicked = useCallback(
    (event: RowClickedEvent<T>) => {
      if (onRowClick && event.rowIndex !== null) {
        onRowClick(event.data as T, event.rowIndex)
      }
    },
    [onRowClick]
  )

  useEffect(() => {
    let isAutoHeight = false
    const h1 =
      tableRef.current?.parentElement?.parentElement?.offsetHeight ?? -1
    const div = document.createElement('div')
    div.style.height =
      (tableRef.current?.parentElement?.parentElement?.offsetHeight ?? 0) +
      1 +
      'px'
    tableRef.current?.parentElement?.parentElement?.appendChild(div)
    const h2 =
      tableRef.current?.parentElement?.parentElement?.offsetHeight ?? -1
    tableRef.current?.parentElement?.parentElement?.removeChild(div)
    if (!minHeight && (h2 > h1 || (h2 === 0 && h1 === 0))) {
      setIsAutoHeightDetected(true)
      isAutoHeight = true
    } else setIsAutoHeightDetected(false)
    if (!tableRef.current || !gridWrapperRef.current) return
    if (isAutoHeight && !minHeight) return
    const parent = tableRef.current
    const gridWrapper = gridWrapperRef.current
    const applyHeight = () => {
      const parentHeight = parent.getBoundingClientRect().height
      const gridWrapperHeight = gridWrapper.getBoundingClientRect().height
      // Guard against collapsed accordion
      if (parentHeight < 50) return
      if (gridWrapper.children?.[0]) {
        const comps = columnDefs
          .filter((col) => col.columnData?.type === 'component')
          ?.map((c) => c.colId ?? '')
        if (comps.length) {
          gridApiRef.current?.autoSizeColumns(comps)
        }
        if (gridWrapperHeight > 50) {
          ;(
            gridWrapper.children[0] as HTMLElement
          ).style.height = `${gridWrapperHeight}px`
        } else
          (
            gridWrapper.children[0] as HTMLElement
          ).style.height = `${parentHeight}px`
      }
    }
    applyHeight()
    const resizeObserver = new ResizeObserver(() => {
      applyHeight()
    })
    resizeObserver.observe(parent)
    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  useDidMountEffect(() => {
    waitForActionsCell()
  }, [data])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            gridWrapperRefDetected.current === false
          ) {
            waitForActionsCellInner()
            gridWrapperRefDetected.current = true
          }
        })
      },
      {
        threshold: 0.01,
      }
    )
    if (gridWrapperRef.current) {
      observer.observe(gridWrapperRef.current)
    }
    return () => {
      if (gridWrapperRef.current) {
        observer.unobserve(gridWrapperRef.current)
      }
    }
  }, [])

  return (
    <CustomAgTableContextProvider
      sort={sort}
      columns={columns}
      headerValues={headerValues}
      onColumnClick={onColumnClick}
      columnsState={columnsState}
      actions={actions}
      onActionClick={onActionClick}
      filterObject={filterObject}
    >
      <div
        className={`ag-theme-alpine ${
          !isEmpty(actions) && stickyActions ? 'sticky-actions' : ''
        }`}
        style={{
          height: '100%',
          width: '100%',
          flex: 1,
          minHeight: minHeight
            ? `${showSubTotal ? 'calc(' + minHeight + ' + 42px)' : minHeight}`
            : '0',
          maxHeight: `${
            showSubTotal ? 'calc(' + maxHeight + ' + 42px)' : maxHeight
          }`,
          visibility:
            isGridReadyToRender && isAutoHeightDetected !== null
              ? 'visible'
              : 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        ref={tableRef as LegacyRef<HTMLDivElement>}
        data-qa-id={dataQaId ?? tableName ?? ''}
      >
        {/* <div className={`${loading ? 'visible' : 'hidden'}`}>
        <SkeletonLoader pagination columns={4} />
      </div>
      <div className={`${loading ? 'hidden' : 'visible'} h-full`}> */}
        {isEmpty(data) && !showTableColumnOnEmpty ? (
          <div className="h-full w-full flex items-center justify-center">
            {children || (
              <span style={{ color: '#666', fontSize: 14 }}>
                {emptyDataMessage || 'No records found'}
              </span>
            )}
          </div>
        ) : (
          <>
            <div className="flex-1 min-h-0" ref={gridWrapperRef}>
              <AgGridReact<T>
                ref={topGrid}
                alignedGrids={[bottomGrid]}
                rowData={data}
                columnDefs={modifiedColumnDefs}
                onGridReady={onGridReady}
                // onFirstDataRendered={handleFirstDataRendered}
                onFilterChanged={handleFilterChanged}
                defaultColDef={defaultColDef}
                gridOptions={gridOptions}
                onColumnHeaderClicked={onColumnHeaderClicked}
                onFilterOpened={onFilterOpened}
                onFilterModified={onFilterModified}
                onColumnMoved={onColumnMoved}
                onColumnResized={onColumnResized}
                loading={loading}
                overlayLoadingTemplate={customOverlayLoadingTemplate}
                overlayNoRowsTemplate={customOverlayNoRowsTemplate}
                suppressRowHoverHighlight={noBackgroundHover}
                onRowClicked={handleRowClicked}
                suppressColumnVirtualisation={true}
                enableCellTextSelection={true}
                ensureDomOrder={true}
                domLayout={
                  !minHeight && isAutoHeightDetected ? 'autoHeight' : undefined
                }
              />
            </div>
            {showSubTotal ? (
              <div className="h-[42px] hide-horizontal-scrollbar overflow-hidden">
                <AgGridReact<T>
                  ref={bottomGrid}
                  rowData={subTotalRowData}
                  columnDefs={subTotalColumnDefs}
                  headerHeight={0}
                />
              </div>
            ) : null}
          </>
        )}
        {/* </div> */}
        {/* <div onClick={() => console.log(gridApiRef.current?.getColumnState())}>
        click me
      </div> */}
        {data?.length > 0 && onPageChange && (
          <Pagination
            currentPage={paginationCurrentPage ?? currentPage}
            totalPages={totalPages ?? 0}
            onPageChange={(page: number, rPerPage: number) => {
              page = rowPerPage !== rPerPage ? 1 : page
              setCurrentPage(page)
              setRowPerPage(rPerPage)
              onPageChange(page, rPerPage)
            }}
            onRowsChange={onRowsChange}
            rowsPer={rowPerPage}
            totalElements={totalElements ?? 0}
            numberOfElements={numberOfElements ?? 0}
          />
        )}
      </div>
    </CustomAgTableContextProvider>
  )
}

export default CustomAgTable

const CustomHeader = <T,>(props: {
  displayName: string | undefined
  column: CustomColDef<T>
  setSort: (sort: SORT_KEYS) => void
  enableFilterButton: boolean | undefined
  showFilter: (target: EventTarget | null) => void
}) => {
  const { displayName, column, setSort, enableFilterButton, showFilter } = props
  const {
    sort,
    columns,
    headerValues,
    columnsState,
    onColumnClick,
    filterObject,
  } = useContext(CustomAgTableContext)
  const newColumn = columns?.find((c) => c.accessor === column.colId)
  const isHidden = columnsState?.find(
    (c) => c.accessor === column.colId
  )?.isHidden
  const currentSort = sort?.key === column.colId ? sort?.isAsc : null
  const handleClick = () => {
    let newSort: SORT_KEYS = SORT_KEYS.Asc

    if (currentSort === 'asc') newSort = SORT_KEYS.Desc
    else if (currentSort === 'desc') newSort = SORT_KEYS.Disabled
    setSort(newSort)
  }

  const sortIcon = useMemo(() => {
    if (currentSort === 'asc') {
      return <SvgSortArrowUser className="rotate-180 cursor-pointer" />
    } else if (currentSort === 'desc') {
      return <SvgSortArrowUser className="cursor-pointer" />
    } else {
      return <SvgDisableSortArrow fill={'#5E5E5E'} className="cursor-pointer" />
    }
  }, [currentSort, newColumn])

  const showMenu = (event: React.MouseEvent) => {
    event.stopPropagation()
    showFilter(event.target)
  }
  const checkFilterApplied = useMemo(() => {
    if (!filterObject?.search_criteria) return false

    const result = Object.values(filterObject.search_criteria).some(
      (item) =>
        item?.search_key?.toLowerCase() ===
        String(props?.column?.columnData?.accessor).toLowerCase()
    )
    return result
  }, [filterObject, props?.column?.columnData?.accessor])

  const isFilterApplied = checkFilterApplied
  return (
    <div
      className="flex items-center cursor-grab select-none gap-2 w-full"
      onClick={enableFilterButton ? handleClick : noop}
    >
      {newColumn ? (
        <div
          className={`flex-1 min-h-0 min-w-0 overflow-hidden ${
            newColumn?.type === columnTypesConst?.CHECKBOX ||
            newColumn?.type === columnTypesConst?.TOGGLE_BUTTON ||
            newColumn?.renderHeader
              ? 'flex justify-center'
              : ''
          }`}
        >
          <TableHeaderColumns
            key={displayName}
            tableName={''}
            column={{
              ...newColumn,
              isHidden: isHidden,
            }}
            onColumnClick={onColumnClick}
            onSortClick={column.onSortClick}
            headerValues={headerValues}
            sort={sort}
            onFilterChange={column.onFilterChange ?? noop}
            // filterObject={filterObject}
            isFilterPresent={false}
            operatorRef={column.operatorRef}
            tabIndex={0}
            isAgGrid={true}
          />
        </div>
      ) : null}
      {enableFilterButton ? sortIcon : null}
      {props.column?.columnData?.filterData &&
      !props.column?.columnData?.isFilterDisabled ? (
        <div className="ml-auto cursor-pointer" onClick={showMenu}>
          <SvgFilter color={isFilterApplied ? '#0A6FCD' : '#5E5E5E'} />
        </div>
      ) : null}
    </div>
  )
}
const TableActionsWrapper = <T,>({
  row,
  rowIndex,
  isDisabled,
}: TableActionsPropsTypes<T>) => {
  const { actions, onActionClick } = useContext(
    CustomAgTableContext as React.Context<CustomAgTableContextType<T>>
  )
  return (
    <TableActions
      row={row}
      rowIndex={rowIndex}
      actions={actions}
      onActionClick={onActionClick || noop}
      setRowPosition={noop}
      isDisabled={isDisabled}
    />
  )
}

interface CustomAgTableContextType<T> {
  sort?: SortObjectType | null
  columns?: TableColumnType<T>[]
  headerValues?: T | undefined
  onColumnClick?: OnColumnClickCallbackType<T> | undefined
  columnsState?: TableColumnType<T>[]
  actions?: TableActionType<T>[]
  onActionClick?: OnActionClickCallbackType<T>
  filterObject?: TableFilterObjectBody
}
interface CustomAgTableContextPropsType<T> {
  children: ReactNode
  sort?: SortObjectType | null
  columns?: TableColumnType<T>[]
  headerValues?: T | undefined
  onColumnClick?: OnColumnClickCallbackType<T> | undefined
  columnsState?: TableColumnType<T>[]
  actions?: TableActionType<T>[]
  onActionClick?: OnActionClickCallbackType<T>
  filterObject?: TableFilterObjectBody
}

export const CustomAgTableContext = createContext<
  CustomAgTableContextType<unknown>
>({})

const CustomAgTableContextProvider = <T,>({
  children,
  sort = null,
  columns = [],
  headerValues,
  onColumnClick = noop,
  columnsState,
  actions = [],
  onActionClick = noop,
  filterObject,
}: CustomAgTableContextPropsType<T>) => {
  const Context = CustomAgTableContext as React.Context<
    CustomAgTableContextType<T>
  >
  const value = useMemo(
    () => ({
      sort,
      columns,
      headerValues,
      onColumnClick,
      columnsState,
      actions,
      onActionClick,
      filterObject,
    }),
    [
      sort,
      columns,
      headerValues,
      onColumnClick,
      columnsState,
      actions,
      onActionClick,
      filterObject,
    ]
  )
  return <Context.Provider value={value}>{children}</Context.Provider>
}

const updateChildren = (operatorDivs: NodeListOf<HTMLDivElement>) => {
  if (
    ['Blank', 'Not blank', 'Is Null', 'Is Not Null'].includes(
      operatorDivs?.[0]?.innerHTML ?? ''
    )
  ) {
    operatorDivs[0].parentElement?.parentElement?.parentElement?.classList.add(
      'blank-operator-filter'
    )
  } else {
    operatorDivs[0].parentElement?.parentElement?.parentElement?.classList.remove(
      'blank-operator-filter'
    )
  }
  operatorDivs.forEach((operatorDiv: HTMLDivElement, index: number) => {
    if (index > 0) {
      if (operatorDiv.parentElement?.parentElement) {
        operatorDiv.parentElement.parentElement.style.display = 'none'
      }
    }
  })
}

const CustomDropdownFilter = forwardRef<
  IFilterComp,
  CustomFilterParams<unknown>
>((props, ref) => {
  const { columns } = useContext(CustomAgTableContext)
  const column = columns?.find(
    (c) => c.accessor === props?.column?.columnData?.accessor
  )
  const search_key =
    column?.filterData?.convertToUppercase === false
      ? (column?.accessor as string)
      : (column?.accessor as unknown as string)?.toUpperCase()
  const model = props.api.getFilterModel()
  const isNull = model?.[props.column.colId ?? '']?.type === 'IS_NULL'
  const selectedValuesFromModel =
    model?.[props.column.colId ?? '']?.filter ?? []
  const matchingValues = selectedValuesFromModel.filter((value: string) =>
    props?.column?.columnData?.filterData?.dropDownOptions?.find(
      (option) => option.value === value
    )
  )
  const allOptions = column?.filterData?.onFocus
    ? []
    : column?.filterData?.dropDownOptions?.sort((a, b) =>
        a.label.localeCompare(b.label)
      ) ?? []
  const [options, setOptions] = useState(allOptions)
  const selectedValuesRef = useRef<string[]>(matchingValues ?? [])
  const isBlankSelectedRef = useRef<boolean>(isNull ? true : false)
  const [selectedValues, setSelectedValues] = useState<string[]>(
    matchingValues ?? []
  )
  const [isBlankSelected, setIsBlankSelected] = useState<boolean>(
    isNull ? true : false
  )
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [loading, setLoading] = useState(false)
  const PAGE_SIZE = 10
  const inputRef = useRef<HTMLInputElement>(null)

  const getFilterOperatorForDropdown = (
    columnData: TableColumnType<unknown> | undefined
  ) => {
    if (
      columnData?.filterData?.dataType ===
        tableFilterType.MULTI_SELECT_DYNAMIC ||
      columnData?.filterData?.dataType === tableFilterType.STRING
    )
      return filterOperatorValues.CONTAINS
    else if (columnData?.filterData?.isMultiSelect)
      return filterOperatorValues.CONTAINS_LIST
    else if (
      columnData?.filterData?.dataType === tableFilterType.STRING_DYNAMIC
    ) {
      return filterOperatorValues.LIKE
    }
    return filterOperatorValues.IN
  }

  const handleFocusOut = useCallback(
    debounce(() => {
      const existingModel = props.api.getFilterModel() || {}
      let columnFilterValue:
        | {
            filterType: string
            type: string
            filter: string[]
          }
        | undefined = undefined

      if (isBlankSelectedRef.current) {
        columnFilterValue = {
          filterType: 'dropdown',
          type: 'IS_NULL',
          filter: [''],
        }
      } else if (selectedValuesRef.current?.length > 0) {
        columnFilterValue = {
          filterType: 'dropdown',
          type: getFilterOperatorForDropdown(
            (props.column as CustomColDef<unknown>)?.columnData
          ),
          filter: selectedValuesRef.current,
        }
      } else {
        columnFilterValue = undefined
      }
      props?.api.setFilterModel({
        ...existingModel,
        [props?.column?.colId ?? '']: columnFilterValue,
      })
    }, 1000),
    []
  )

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      getDropdownOptionsOnFocus(value, 1, PAGE_SIZE)
    }, 700),
    []
  )

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (column?.filterData?.onFocus) {
      setPage(1)
      setOptions([])
      debouncedSearch(e.target.value)
    } else
      setOptions(
        allOptions.filter((item) =>
          item.label
            .toLocaleLowerCase()
            .includes(e.target.value.toLocaleLowerCase())
        )
      )
  }

  const handleSelectAll = () => {
    setIsBlankSelected(false)
    isBlankSelectedRef.current = false
    const visibleValues = options.map((e: { value: string }) => e.value)
    const allVisibleSelected = visibleValues.every((e: string) =>
      selectedValues.includes(e)
    )

    if (allVisibleSelected) {
      setSelectedValues((prev) =>
        prev.filter((v) => !visibleValues.includes(v))
      )
      selectedValuesRef.current = selectedValuesRef.current.filter(
        (v) => !visibleValues.includes(v)
      )
    } else {
      setSelectedValues((prev) => [
        ...prev,
        ...visibleValues.filter((e: string) => !prev.includes(e)),
      ])
      selectedValuesRef.current = [
        ...selectedValuesRef.current,
        ...visibleValues.filter(
          (e: string) => !selectedValuesRef.current.includes(e)
        ),
      ]
    }
  }

  const handleBlankSelect = () => {
    if (isBlankSelected) {
      setIsBlankSelected(false)
      isBlankSelectedRef.current = false
    } else {
      setIsBlankSelected(true)
      isBlankSelectedRef.current = true
      setSelectedValues([])
      selectedValuesRef.current = []
    }
  }

  const handleCheckboxChange = (value: string) => {
    setIsBlankSelected(false)
    isBlankSelectedRef.current = false
    setSelectedValues((prev) =>
      prev.includes(value) ? prev.filter((e) => e !== value) : [...prev, value]
    )
    selectedValuesRef.current = selectedValuesRef.current.includes(value)
      ? selectedValuesRef.current.filter((e) => e !== value)
      : [...selectedValuesRef.current, value]
  }

  const removeDropdownOption = () => {
    setSelectedValues([])
    selectedValuesRef.current = []
    setIsBlankSelected(false)
    isBlankSelectedRef.current = false
  }
  const isSelected =
    options.length > 0 &&
    options.every((e: { value: string }) => selectedValues.includes(e.value))

  const doesFilterPass = useCallback((p: IDoesFilterPassParams) => {
    if (selectedValuesRef.current.length === 0) return true
    if (isBlankSelectedRef.current) {
      return !!p.data?.[props?.column?.colId ?? '']
    }
    return selectedValuesRef.current.includes(
      p.data?.[props?.column?.colId ?? '']
    )
  }, [])

  useImperativeHandle(ref, () => ({
    getModel() {
      if (isBlankSelectedRef.current)
        return {
          filterType: 'dropdown',
          type: 'IS_NULL',
          filter: [''],
        }

      if (selectedValuesRef.current.length > 0)
        return {
          filterType: 'dropdown',
          type: getFilterOperatorForDropdown(
            (props.column as CustomColDef<unknown>).columnData
          ),
          filter: selectedValuesRef.current,
        }

      return null
    },

    setModel(model) {
      if (!model) {
        selectedValuesRef.current = []
        isBlankSelectedRef.current = false
        setSelectedValues([])
        setIsBlankSelected(false)
        return
      }

      if (model.type === 'IS_NULL') {
        isBlankSelectedRef.current = true
        selectedValuesRef.current = []
        setSelectedValues([])
        setIsBlankSelected(true)
        return
      }

      selectedValuesRef.current = model.filter ?? []
      setSelectedValues(model.filter ?? [])
      isBlankSelectedRef.current = false
      setIsBlankSelected(false)
    },
    doesFilterPass,
    getGui: () => new HTMLElement(),
    isFilterActive: () => false,
  }))

  const getDropdownOptionsOnFocus = async (
    searchTerm: string,
    page: number,
    dataSize: number
  ) => {
    if (!column?.filterData?.onFocus) return
    if (loading) return
    setLoading(true)
    const searchCriteria = {
      search_key: search_key,
      operator: filterOperatorValues.LIKE,
      search_value: [searchTerm],
    }
    const { data, size } = await column.filterData.onFocus(
      searchCriteria,
      page,
      dataSize
    )
    setOptions((prev) => {
      const newOptions = data.map((val: string) => ({
        label: column?.filterData?.format
          ? formatValueWithPattern(val, column?.filterData.format)
          : val,
        value: val,
      }))
      const merged = [...prev, ...newOptions]
      const unique = Array.from(
        new Map(merged.map((item) => [item.value, item])).values()
      )
      return unique
    })
    setHasMore(size < PAGE_SIZE || data.length === 0 ? false : true)
    setLoading(false)
  }

  const observer = useRef<IntersectionObserver | null>(null)

  const lastItemRef = useCallback(
    (node: HTMLElement | null) => {
      if (loading) return
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            setPage((prev) => prev + 1)
          }
        },
        {
          root: null,
          rootMargin: '0px',
          threshold: 1.0,
        }
      )
      if (node) observer.current.observe(node)
    },
    [loading, options.length, hasMore]
  )

  useEffect(() => {
    if (column?.filterData?.onFocus) {
      getDropdownOptionsOnFocus(inputRef?.current?.value ?? '', page, PAGE_SIZE)
    }
  }, [page])

  useEffect(() => {
    // change with usedidmounteffect
    handleFocusOut()
  }, [selectedValues, isBlankSelected])

  useGridFilter({ doesFilterPass })

  return (
    <div className="h-80 p-2 relative flex flex-col custom-ag-dropdown">
      <div className="flex py-1 px-2 bg-white items-center rounded-sm border border-accent-100 gap-1">
        <SvgSearchIcon
          className="text-accent-400"
          height="0.7rem"
          width="0.7rem"
        />
        <input
          type="text"
          onChange={handleSearch}
          className="outline-none flex-1 min-w-0"
          placeholder="Search..."
          ref={inputRef}
        />
      </div>
      <div className="mt-2 flex-1 min-h-0 flex flex-col gap-1 px-1 overflow-y-auto pb-2">
        <button className="flex items-center gap-2" onClick={handleBlankSelect}>
          <input type="checkbox" checked={isBlankSelected} />
          <span>{'(Blanks)'}</span>
        </button>
        <button className="flex items-center gap-2" onClick={handleSelectAll}>
          <input type="checkbox" checked={isSelected} />
          <span>{'(Select All)'}</span>
        </button>
        {options?.map(
          (item: { label: string; value: string }, index: number) => (
            <button
              className="flex items-center gap-2"
              key={`indexed-item-${item.value}`}
              onClick={() => handleCheckboxChange(item.value)}
              ref={index === options.length - 1 ? lastItemRef : null}
            >
              <input
                type="checkbox"
                checked={selectedValues.includes(item.value)}
              />
              <span>{item.label}</span>
            </button>
          )
        )}
        {loading && <div>Loading...</div>}
      </div>
      <div className="flex self-end mt-2">
        <CustomButton
          variant="secondary"
          size="small"
          onClick={removeDropdownOption}
        >
          {NAMESPACE.CLEAR}
        </CustomButton>
      </div>
    </div>
  )
})
CustomDropdownFilter.displayName = 'CustomDropdownFilter'

const CustomBooleanFilter = forwardRef<
  IFilterComp,
  CustomFilterParams<unknown>
>((props, _ref) => {
  const model = props.api.getFilterModel()?.[props.column.colId ?? '']
  const options = (
    props?.column?.columnData?.filterData?.booleanOptions ??
    BOOLEAN_FILTER_OPTIONS
  ).map((option) => ({
    ...option,
    checked: !model ? option.label === 'All' : option.value === model.filter[0],
    search_value: option.search_value,
  }))
  const optionsWithNull = [
    {
      value: 'IS_NULL',
      label: 'Is Null',
      search_value: null,
      checked: !model ? false : model.type === 'IS_NULL',
    },
    {
      value: 'IS_NOT_NULL',
      label: 'Is Not Null',
      search_value: null,
      checked: !model ? false : model.type === 'IS_NOT_NULL',
    },
  ]
  const [allOptions, setAllOptions] = useState([...options, ...optionsWithNull])
  const allOptionsRef = useRef([...options, ...optionsWithNull])

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const newOptions = allOptions.map((option) => ({
      ...option,
      checked: option.value === value,
    }))
    setAllOptions(newOptions)
    allOptionsRef.current = newOptions
    const existingModel = props.api.getFilterModel() || {}
    const columnFilterValue =
      value === options.find((o) => o.label === 'All')?.value
        ? undefined
        : {
            filterType: 'boolean',
            type:
              value === 'IS_NULL' || value === 'IS_NOT_NULL'
                ? value
                : filterOperatorValues.EQUALS_BOOLEAN,
            filter:
              value === 'IS_NULL' || value === 'IS_NOT_NULL'
                ? ['']
                : [
                    String(
                      options.find((o) => o.value === value)?.search_value
                    ) ?? '',
                  ],
          }
    props?.api.setFilterModel({
      ...existingModel,
      [props?.column?.colId ?? '']: columnFilterValue,
    })
  }

  const doesFilterPass = useCallback(() => {
    return true
  }, [])

  useGridFilter({ doesFilterPass })

  return (
    <div className="p-2">
      <CustomRadioGroup
        options={allOptions}
        name={``}
        labelClasses="break-all text-xs"
        flexDirection="flex-col"
        parentClasses="!py-1"
        onChange={onChange}
      />
    </div>
  )
})
CustomBooleanFilter.displayName = 'CustomBooleanFilter'

const isDateOperator = (operator: string) => operator === tableFilterType.DATE

const isDateTimeOperator = (operator: string) =>
  operator === tableFilterType.DATE_TIME

const isTimeOperator = (operator: string) => operator === tableFilterType.TIME

const isDateTimeDynamicOperator = (operator: string) =>
  operator === tableFilterType.DATE_TIME_DYNAMIC

const isStringOperator = (operator: string) =>
  operator === tableFilterType.STRING

const isStringDynamicOperator = (operator: string) =>
  operator === tableFilterType.STRING_DYNAMIC

const isStringTypeOperator = (operator: string) =>
  isStringOperator(operator) || isStringDynamicOperator(operator)

const isDateTypeOperator = (operator: string) =>
  isDateOperator(operator) ||
  isDateTimeOperator(operator) ||
  isTimeOperator(operator) ||
  isDateTimeDynamicOperator(operator)

const isRangeOperator = (operator: string) =>
  operator === filterOperatorValues.BETWEEN ||
  operator === filterOperatorValues.NOT_BETWEEN

const getTimeInUTC = (time: string): string => {
  const currentDateString = new Date().toISOString().split('T')[0]
  const fullDateTime = `${currentDateString}T${time}Z`
  const date = new Date(fullDateTime)
  const timezoneOffset = date.getTimezoneOffset()
  return new Date(date.getTime() + timezoneOffset * 60000)
    .toISOString()
    .split('.')[0]
    .split('T')[1]
}

const getDynamicDate = (val: string, operator: string) => {
  let time = moment(new Date(val).toISOString())
  // Add one minute for isBeforeOrEqualTo as input only takes values upto minutes
  // but actual values are upto mili seconds
  if (
    operator === filterOperatorValues.IS_BEFORE_OR_EQUAL_TO ||
    operator === filterOperatorValues.IS_AFTER
  )
    time = time.add(1, 'minutes').subtract(1, 'seconds')
  return time.utc().format('YYYY-MM-DDTHH:mm:ssZ')
}

interface CustomFilterComp<T> extends IFilterComp<T> {
  state: {
    model: {
      type?: string
      conditions?: { type: string }[]
    }
    valid: boolean
  }
  filterListOptions: FilterOptionsType[]
}

interface FilterOptionsType {
  value: string
  text: string
}

function useDidMountEffect(callback: () => void, deps: unknown[] = []): void {
  const didMount = useRef(false)

  useEffect(() => {
    if (didMount.current) {
      callback()
    } else {
      didMount.current = true
    }
  }, deps)
}
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))
